/**
 * Feature Flags: Enabled state methods.
 */

import { followAxiosLink } from '../..';
import { getLogger } from '../../../logging';
import { CollectionType } from '../../types';
import { createLogMsgFormatter, FEATURE_FLAGS_LOGGER_COMPONENT } from './common';
import { getRootLink } from './rootLinks';

const logger = getLogger(FEATURE_FLAGS_LOGGER_COMPONENT);
const L = createLogMsgFormatter('enabled states');

/**
 * Dictionary of feature enabled states, where key is the feature id and value is a
 * boolean representing the enabled state.
 */
export type EnabledDict<T extends string[]> = Record<T[number], boolean>;

const LIMIT = 2147483647; // maximum positive value for a 32-bit signed binary integer

// *** public methods ***

let cachedInsideSasPromise: Promise<boolean> | null = null;

/**
 * Queries whether the environment is inside SAS.
 * Result is cached to prevent unnecessary network traffic.
 * @returns True if environment is inside SAS; false otherwise.  There is a bias to a false
 * negative, meaning it is possible (although rare) to return false when in SAS.
 */
export function isInsideSas(): Promise<boolean> {
  if (!cachedInsideSasPromise) {
    logger.info(L('retrieving insideSas...'));

    cachedInsideSasPromise = getRootLink('insideSas')
      .then(async (link) => {
        if (!link) {
          throw new Error("cannot find 'insideSas' link relation in featureFlags root request");
        }

        const resp = await followAxiosLink<InsideSasSvcObj>(link);
        logger.info(L('insideSas received'));

        return resp.data?.insideSas === true;
      })
      .catch((err) => {
        logger.warn(L(err));
        logger.warn(L('ignoring errors and assuming not inside SAS'));

        // ignore service errors
        return false;
      });
  }

  return cachedInsideSasPromise;
}

/**
 * Query whether a feature is enabled for the current user.
 * Enabled states are cached to prevent unnecessary network traffic.
 * @param featureId Id of the desired feature to query.
 * @returns True if feature exists and is enabled for the current user; false otherwise.
 * If the service is down, false is returned.
 * @example
 * ```
 * await enabledState = isFeatureEnabled('MyFeature');
 * ```
 */
export async function isFeatureEnabled(featureId: string): Promise<boolean> {
  const enabledFeatureSet = await getAndCacheAllEnabledFeatures();
  return enabledFeatureSet.has(featureId);
}

/**
 * Query whether multiple features are enabled for the current user.
 * Enabled states are cached to prevent unnecessary network traffic.
 * @param featureIds Variable argument list of ids of the desired features to query.
 * @returns Dictionary where keys are the provided feature ids and values are boolean enabled states.
 * Each state will be true if feature exists and is enabled for the current user; false otherwise.
 * If the service is down, false is returned for each feature id.
 * @example
 * ```
 * await enabledStateDict = areFeaturesEnabled('MyFeature1', 'MyFeature2');
 * if (enabledStateDict.MyFeature1) {
 *     // MyFeature1 is enabled
 * }
 * ```
 */
export async function areFeaturesEnabled<Args extends string[]>(
  ...featureIds: Args
): Promise<EnabledDict<Args>> {
  const enabledFeatureSet = await getAndCacheAllEnabledFeatures();
  return featureIds.reduce((enabledFeatureMap: EnabledDict<Args>, featureId: Args[number]) => {
    enabledFeatureMap[featureId] = enabledFeatureSet.has(featureId);
    return enabledFeatureMap;
  }, {} as EnabledDict<Args>);
}

/**
 * Query all known enabled features for the current user.
 * Enabled states are cached to prevent unnecessary network traffic.
 * @returns Set of feature ids that exist and are enabled for the current user.
 * If the service is down, an empty set is returned.
 * @example
 * ```
 * await enabledFeatureIds = getAllEnabledFeatures();
 * if (enabledFeatureIds.has('MyFeature')) {
 *     // MyFeature is enabled
 * }
 * ```
 */
export async function getAllEnabledFeatures(): Promise<Set<string>> {
  const enabledFeatureSet = await getAndCacheAllEnabledFeatures();
  return new Set([...enabledFeatureSet]); // return a copy
}

// *** private methods ***

// Represents objects returned via /featureFlags/insideSas.
interface InsideSasSvcObj {
  version?: number;
  insideSas?: boolean;
}

// Represents objects returned via /featureFlags/enabled.items.
interface EnabledSvcObj {
  version: number;
  id: string;
  enabled: boolean;
}

// Only contains enabled feature ids; missing feature ids are assumed disabled.
let cachedEnabledFeatureSetPromise: Promise<Set<string>> | null = null;

// Retrieves and caches all enabled feature ids.
function getAndCacheAllEnabledFeatures(): Promise<Set<string>> {
  if (!cachedEnabledFeatureSetPromise) {
    logger.info(L('retrieving...'));

    cachedEnabledFeatureSetPromise = getRootLink('enabledTrueBulk')
      .then((link) => {
        if (!link) {
          throw new Error(
            "cannot find 'enabledTrueBulk' link relation in featureFlags root request"
          );
        }

        return followAxiosLink<CollectionType<EnabledSvcObj>>(link, {
          params: {
            limit: LIMIT,
          },
        });
      })
      .then((resp) => {
        logger.info(L('received'));

        const items = resp.data?.items;
        if (!Array.isArray(items)) {
          throw new Error("cannot find 'items' array in featureFlags 'enabledBulk' link request");
        }

        return items.reduce((enabledFeatureSet: Set<string>, svcObj: EnabledSvcObj) => {
          // only care about enabled features, not disabled features
          if (svcObj.enabled === true) {
            enabledFeatureSet.add(svcObj.id);
          }
          return enabledFeatureSet;
        }, new Set());
      })
      .catch((err) => {
        logger.warn(L(err));
        logger.warn(L('ignoring errors and assuming no enabled states'));

        // ignore service errors
        return new Set();
      });
  }

  return cachedEnabledFeatureSetPromise;
}

// Invalidates the cache for the enabled state methods.
/** @internal */
export function clearEnabledCache() {
  logger.info(L('clearing caches'));
  cachedInsideSasPromise = null;
  cachedEnabledFeatureSetPromise = null;
}
